/**
 * The word as squares.
 *
 * AriW9500 is a pixel font on an 11 px grid (unitsPerEm 2048, one font pixel
 * = 186.18 units): drawn at font-size 11px on integer coordinates, every font
 * pixel lands on exactly one canvas pixel, so reading the alpha channel back
 * gives the squares of the wordmark ("danino" ≈ 32 × 9).
 */
const FONT_SIZE = 11
const FONT = `${FONT_SIZE}px AriW9500`
const PADDING = 2

const cache = new Map()

/**
 * @param {string} word
 * @returns {Promise<{cols: number, rows: number, cells: Array<[number, number]>}>}
 *   cells relative to the word's top-left corner
 */
export async function rasterizeWord(word) {
  if (cache.has(word)) return cache.get(word)

  let fontReady = false
  try {
    fontReady = (await document.fonts.load(FONT, word)).length > 0
  } catch {
    // No font loading API or the file failed: rasterise the fallback font.
  }

  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d', { willReadFrequently: true })
  if (!context) return { cols: 0, rows: 0, cells: [] }

  context.font = FONT
  const width = Math.ceil(context.measureText(word).width) + PADDING * 2
  const height = FONT_SIZE * 2
  canvas.width = width
  canvas.height = height

  // Resizing the canvas resets its state.
  context.font = FONT
  context.textBaseline = 'alphabetic'
  context.fillStyle = '#000'
  context.fillText(word, PADDING, FONT_SIZE + PADDING)

  const { data } = context.getImageData(0, 0, width, height)
  const points = []
  let minX = width
  let minY = height
  let maxX = -1
  let maxY = -1

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] < 128) continue
      points.push([x, y])
      minX = Math.min(minX, x)
      minY = Math.min(minY, y)
      maxX = Math.max(maxX, x)
      maxY = Math.max(maxY, y)
    }
  }

  const result = points.length
    ? {
        cols: maxX - minX + 1,
        rows: maxY - minY + 1,
        cells: points.map(([x, y]) => [x - minX, y - minY]),
      }
    : { cols: 0, rows: 0, cells: [] }

  // A fallback-font result is only good until the real font is there.
  if (fontReady) cache.set(word, result)
  return result
}
